import { ref } from 'vue';

export interface UseBottomSheetOptions {
  onClose?: () => void;
  swipeThreshold?: number;
}

export function useBottomSheet(options: UseBottomSheetOptions = {}) {
  const { onClose, swipeThreshold = 100 } = options;

  const isOpen = ref(false);
  const isDragging = ref(false);
  const isScrollingInner = ref(false);
  const touchStartY = ref(0);
  const touchEndY = ref(0);
  const dragOffset = ref(0);

  const sheetRef = ref<HTMLElement | null>(null);
  const scrollInnerRef = ref<HTMLElement | null>(null);

  const handleTouchStart = (e: TouchEvent) => {
    touchStartY.value = e.touches[0].clientY;
    isDragging.value = true;

    const scrollInner = scrollInnerRef.value;
    if (scrollInner) {
      const touchTarget = e.target as HTMLElement;
      isScrollingInner.value = scrollInner.contains(touchTarget);
    }
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (!isDragging.value || isScrollingInner.value) return;

    touchEndY.value = e.touches[0].clientY;
    const diff = touchEndY.value - touchStartY.value;

    if (diff > 0 && sheetRef.value) {
      dragOffset.value = diff;
      sheetRef.value.style.transform = `translateY(${Math.min(diff, 300)}px)`;
      sheetRef.value.style.transition = 'none';
    }
  };

  const resetState = () => {
    isDragging.value = false;
    isScrollingInner.value = false;
    dragOffset.value = 0;
    touchStartY.value = 0;
    touchEndY.value = 0;
  };

  const close = () => {
    if (sheetRef.value) {
      sheetRef.value.style.transform = 'translateY(100%)';
      sheetRef.value.style.transition = 'transform 0.3s ease-in-out';
    }

    setTimeout(() => {
      isOpen.value = false;
      onClose?.();
      resetState();
    }, 300);
  };

  const handleTouchEnd = () => {
    if (!isDragging.value) return;

    if (dragOffset.value > swipeThreshold) {
      close();
    } else if (sheetRef.value) {
      sheetRef.value.style.transform = 'translateY(0)';
      sheetRef.value.style.transition = 'transform 0.3s ease';
    }

    resetState();
  };

  const open = () => {
    isOpen.value = true;
    resetState();
  };

  const toggle = () => (isOpen.value ? close() : open());

  return {
    isOpen,
    isDragging,
    sheetRef,
    scrollInnerRef,
    open,
    close,
    toggle,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    resetState,
  };
}
